import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { faAngleUp, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link as RouterLink } from "react-router-dom";
import shortid from "shortid";

import rem from "utils/rem";

interface Data {
  id: number;
  name: JSX.Element | string;
  value: any;
  key?: any;
}

interface DataTableProps {
  headers: string[];
  data: Data[];
  link?: string;
}

export const mapData = (items: any, isToFixed: boolean = false) =>
  Object.keys(items).map((key: string, index: number) => ({
    id: ++index,
    name: key,
    value: isToFixed ? items[key].toFixed(2) : items[key],
    key,
  }));

export const mapData2 = (items: any, isToFixed: boolean = false) =>
  Object.keys(items).map((key: string) => ({
    name: key,
    value: isToFixed ? items[key].toFixed(2) : items[key],
  }));

export const formatData = (
  items: any,
  isToFixed: boolean = false,
  format?: (item: string) => JSX.Element
) =>
  Object.keys(items).map((key: string, index: number) => ({
    id: ++index,
    name: format(key),
    value: isToFixed ? items[key].toFixed(2) : items[key],
    key,
  }));

export const sortData = <T,>(array: T[], key: string) =>
  array.sort((a, b) => b[key] - a[key]);

const DataTable: React.FC<DataTableProps> = ({ headers = [], data, link }) => {
  const [items, setItems] = useState<Array<Data>>([]);
  const [isSorted, setIsSorted] = useState(false);
  useEffect(() => setItems([...data]), [data]);

  const handleClick = () => {
    if (!isSorted) {
      setItems((items) => [...items.sort((a, b) => b.value - a.value)]);
      setIsSorted(true);
    } else {
      setItems([...data]);
      setIsSorted(false);
    }
  };

  return (
    <Table>
      <thead>
        <tr>
          {headers.map((header, index) => (
            <TH
              onClick={() => index > 0 && handleClick()}
              key={shortid.generate()}
            >
              {header}{" "}
              {index > 0 && (
                <FontAwesomeIcon
                  icon={isSorted ? (faAngleUp as any) : (faAngleDown as any)}
                />
              )}
            </TH>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map(({ id, name, value, key }) => (
          <TR key={id}>
            <td>{link ? <Link to={`${link}${key}`}>{id}</Link> : id}</td>
            <td>{link ? <Link to={`${link}${key}`}>{name}</Link> : name}</td>
            <td>{link ? <Link to={`${link}${key}`}>{value}</Link> : value}</td>
          </TR>
        ))}
      </tbody>
    </Table>
  );
};

const Table = styled.table`
  margin: 1rem 0;
  text-align: left;
  border-collapse: collapse;
  width: 100%;

  thead {
    background-color: black;
    color: white;
  }

  & td:first-child {
    width: 0.8rem;
    text-align: center;
  }

  & td,
  & th {
    border: ${rem(1)} solid #ddd;
    padding: 0.4rem 0.5rem;
  }

  & th {
    border: ${rem(1)} solid #000;
  }
`;

const TR = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.columnAlternateColor};
  }

  &:hover {
    background-color: ${({ theme }) => theme.columnHoverColor};
  }
`;

const TH = styled.th`
  cursor: pointer;
`;

interface Props {
  theme: { text: string };
}

const Link = styled(RouterLink)`
  text-decoration: none;
  color: ${({ theme }: Props) => theme.text};

  span {
    color: ${({ theme }: Props) => theme.text};
  }
`;

export default DataTable;
